import React, { useEffect, useMemo, useState } from "react";
import { Statistic, Table, Alert, Spin } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import request from "../../utils/request";
import CameraYandexMap from "./CameraYandexMap";

const firstDefined = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== undefined && values[i] !== null) return values[i];
  }
  return undefined;
};

const pickCoords = (item) => {
  if (Array.isArray(item.coordinates) && item.coordinates.length === 2) {
    return item.coordinates.map(Number);
  }
  const lat = firstDefined(item.lat, item.latitude, item.y);
  const lng = firstDefined(
    item.lng,
    item.lon,
    item.long,
    item.longitude,
    item.x
  );
  if (lat != null && lng != null) {
    const parsed = [Number(lat), Number(lng)];
    if (parsed.every((n) => Number.isFinite(n))) return parsed;
  }
  return null;
};

const pickName = (item, index) =>
  item.name ||
  item.title ||
  item.label ||
  item.cameraName ||
  (item.id != null ? "Камера #" + item.id : "Камера " + (index + 1));

const normalize = (raw) =>
  raw
    .map((item, index) => {
      const coords = pickCoords(item);
      if (!coords) return null;
      const key =
        item.id != null
          ? String(item.id)
          : coords[0] + "," + coords[1] + "," + index;
      return {
        key,
        id: item.id != null ? item.id : index + 1,
        name: pickName(item, index),
        latitude: coords[0],
        longitude: coords[1],
        address: item.address || item.location || item.place || "",
      };
    })
    .filter(Boolean);

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.content)) return payload.content;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
};

const formatCoord = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(6)
    : value;

const CameraMap = () => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    request("/cameramap/list")
      .then((result) => {
        if (cancelled) return;
        setCameras(normalize(extractList(result)));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          (err && err.message) || "Не удалось загрузить список видеокамер"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo(
    () => [
      { title: "№", key: "index", width: 60, render: (_v, _r, i) => i + 1 },
      { title: "Название", dataIndex: "name", key: "name" },
      {
        title: "Адрес",
        dataIndex: "address",
        key: "address",
        render: (value) => value || "—",
      },
      {
        title: "Широта",
        key: "lat",
        width: 140,
        render: (_v, record) => formatCoord(record.latitude),
      },
      {
        title: "Долгота",
        key: "lng",
        width: 140,
        render: (_v, record) => formatCoord(record.longitude),
      },
    ],
    []
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Statistic
          title="Всего камер"
          value={cameras.length}
          prefix={<VideoCameraOutlined />}
        />
      </div>

      {error && <Alert type="error" message={error} showIcon />}

      <Spin spinning={loading} tip="Загрузка...">
        <CameraYandexMap cameraMaps={cameras} />
        <Table
          rowKey="key"
          columns={columns}
          dataSource={cameras}
          size="small"
          scroll={{ x: 600 }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          style={{ marginTop: 16 }}
        />
      </Spin>
    </div>
  );
};

export default CameraMap;
